'use client'

import { useEffect, useState } from 'react'
import { CategoryMenuFixed } from './layout/category-menu-fixed'
import { config } from '@/lib/config'

// Cache configuration
const MENU_CACHE_KEY = 'hinch_menu_config'
const MENU_CACHE_DURATION = 2 * 60 * 1000 // 2 minutes in ms

// Get cached menu from localStorage
function getCachedMenu(): MenuItem[] | null {
  if (typeof window === 'undefined') return null
  
  try {
    const cached = localStorage.getItem(MENU_CACHE_KEY)
    if (!cached) return null
    
    const { data, timestamp } = JSON.parse(cached)
    const isExpired = Date.now() - timestamp > MENU_CACHE_DURATION
    
    if (isExpired) {
      localStorage.removeItem(MENU_CACHE_KEY)
      return null
    }
    
    return data
  } catch {
    return null
  }
}

// Save menu to localStorage
function setCachedMenu(data: MenuItem[]) {
  if (typeof window === 'undefined') return
  
  try {
    localStorage.setItem(MENU_CACHE_KEY, JSON.stringify({
      data,
      timestamp: Date.now()
    }))
  } catch {
    // Ignore localStorage errors (quota exceeded, etc.)
  }
}

export interface MenuItem {
  name: string
  handle: string
  sub_menus: MenuItem[]
  category_id?: string
  visible?: boolean
}

// Drop nodes flagged visible: false in the Website Config JSON (with their
// children). A missing flag counts as visible so unflagged data keeps working.
function filterVisibleMenu(items: MenuItem[]): MenuItem[] {
  return items
    .filter(item => item.visible !== false)
    .map(item => ({ ...item, sub_menus: filterVisibleMenu(item.sub_menus || []) }))
}

interface CategoryMenuWrapperProps {
  onMenuOpen?: () => void;
  initialMenuData?: MenuItem[];
}

export function CategoryMenuWrapper({ onMenuOpen, initialMenuData = [] }: CategoryMenuWrapperProps) {
  // If we have server data, use it directly - no state changes on mount to prevent flicker
  const hasServerData = initialMenuData.length > 0
  const [menuData, setMenuData] = useState<MenuItem[]>(initialMenuData)
  
  // Cache the server data on mount, or fetch if no server data
  useEffect(() => {
    if (hasServerData) {
      // Server provided data - just cache it for future use
      setCachedMenu(initialMenuData)
      return
    }
    
    // No server data - try cache first
    const cached = getCachedMenu()
    if (cached && cached.length > 0) {
      setMenuData(cached)
      return
    }
    
    // No cache - fetch from API
    const loadMenuConfig = async () => {
      try {
        const backendUrl = config.backend.url

        const response = await fetch(`${backendUrl}/store/website-config/menu_config`, {
          headers: {
            'x-publishable-api-key': config.publishableApiKey
          }
        })

        if (!response.ok) return

        const data = await response.json()
        if (data.config?.config_data?.menus) {
          const menus = data.config.config_data.menus
          setCachedMenu(menus)
          setMenuData(menus)
        } else if (data.config?.config_data) {
          setCachedMenu(data.config.config_data)
          setMenuData(data.config.config_data)
        }
      } catch {
        // Silently fail - menu is optional
      }
    }

    loadMenuConfig()
  }, [hasServerData, initialMenuData])

  // If we have server data, render immediately - NO loading state, NO flicker
  if (hasServerData) {
    return <CategoryMenuFixed menuData={filterVisibleMenu(initialMenuData)} onMenuOpen={onMenuOpen} />
  }

  // Client-side only - use local state (from cache or fetch)
  // No skeleton - just render nothing until data is available (prevents flicker)
  if (menuData.length === 0) {
    return null
  }

  return <CategoryMenuFixed menuData={filterVisibleMenu(menuData)} onMenuOpen={onMenuOpen} />
}
